// Importing necessary libraries
import * as FileSaver from "file-saver"; // For saving files
import * as XLSX from "xlsx"; // For handling Excel files
import isEmpty from "lodash/isEmpty"; // Utility to check if an object is empty
import get from "lodash/get"; // Utility to safely access object properties
import moment from "moment"; // For date and time manipulation
import { addMonths, subMonths, format } from "date-fns";

// Function to export data to CSV
export const exportToCSV = (
  mappedLogs,
  fileName,
  chipValue,
  exportedBy,
  exportedByFname,
  exportedByLname,
) => {
  const myCurrentDate = new Date();
  const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  // const date = `${
  //   myCurrentDate.getMonth() + 1
  // }/${myCurrentDate.getDate()}/${myCurrentDate.getFullYear()} ${myCurrentDate.getHours()}:${myCurrentDate.getMinutes()}:${myCurrentDate.getSeconds()}`;
  // const newCurrentDate = date;
  const fileType =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
  const fileExtension = ".xlsx";

  // Create a worksheet from the data
  const ws = XLSX.utils.json_to_sheet(
    !isEmpty(mappedLogs) ? mappedLogs : [{ "No Records Found": "" }],
    { origin: "A6" },
  );

  // Add metadata to the worksheet
  XLSX.utils.sheet_add_aoa(ws, [["Module:"]], { origin: "A1" });
  XLSX.utils.sheet_add_aoa(ws, [[`${fileName}`]], { origin: "B1" });
  XLSX.utils.sheet_add_aoa(ws, [["Exported on:"]], { origin: "A2" });
  XLSX.utils.sheet_add_aoa(
    ws,
    [
      [
        `${moment(myCurrentDate)
          .tz(localTimeZone)
          .format("MM/DD/YYYY hh:mm:ss A")}`,
      ],
    ],
    { origin: "B2" },
  );
  XLSX.utils.sheet_add_aoa(ws, [["Exported by:"]], { origin: "A3" });
  XLSX.utils.sheet_add_aoa(
    ws,
    [[`${exportedByFname} ${exportedByLname} <${exportedBy}>`]],
    { origin: "B3" },
  );

  // Add filters and adjust column widths
  if (!isEmpty(mappedLogs)) {
    ws["!autofilter"] = {
      ref: `A6:${XLSX.utils.encode_col(
        Object.keys(mappedLogs[0]).length - 1,
      )}6`,
    };
    ws["!sort"] = [{ column: 1, order: "asc" }];
  }
  // Calculate the maximum content length for each column
  const columnWidths = [];
  XLSX.utils.sheet_to_json(ws, { header: 1 }).forEach((row) => {
    row.forEach((cell, colIndex) => {
      const cellLength = cell ? String(cell).length : 10; // Default width if cell is empty
      columnWidths[colIndex] = Math.min(
        Math.max(columnWidths[colIndex] || 0, cellLength),
        25,
      );
    });
  });

  // Set the width for each column based on the maximum content length
  ws["!cols"] = columnWidths.map((width) => ({ wch: width }));

  XLSX.utils.sheet_add_aoa(ws, [["Filters:"]], { origin: "A4" });
  XLSX.utils.sheet_add_aoa(ws, [[`${chipValue}`]], { origin: "B4" });
  const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
  const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const data = new Blob([excelBuffer], { type: fileType });
  FileSaver.saveAs(
    data,
    `${fileName}${moment(myCurrentDate)
      .tz(localTimeZone)
      .format("MMDDYYYYhhmmss")}${fileExtension}`,
  );
};

export const exportToCSVProvider = (
  mappedLogs,
  fileName,
  chipValue,
  exportedBy,
  exportedByFname,
  exportedByLname,
) => {
  const workbook = { SheetNames: [], Sheets: {} };
  const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const currentDate = new Date();
  const shortTimeZone = new Intl.DateTimeFormat("en-US", {
    timeZone: localTimeZone,
    timeZoneName: "short",
  })
    .formatToParts(currentDate)
    .find((part) => part.type === "timeZoneName")?.value;
  const timeZoneAbbreviation = /^GMT[+-]/.test(shortTimeZone || "")
    ? new Intl.DateTimeFormat("en-US", {
        timeZone: localTimeZone,
        timeZoneName: "long",
      })
        .formatToParts(currentDate)
        .find((part) => part.type === "timeZoneName")
        ?.value.split(" ")
        .map((word) => word[0])
        .join("")
    : shortTimeZone;

  const filtersText = Array.isArray(chipValue)
    ? chipValue
        .filter(
          (filter) => filter !== null && filter !== undefined && filter !== "",
        )
        .join("; ")
    : chipValue || "";

  // Create an empty worksheet and add metadata at the top
  const worksheet = XLSX.utils.aoa_to_sheet([
    ["Module:", fileName],
    [
      "Exported on:",
      `${moment(currentDate).tz(localTimeZone).format("MM/DD/YYYY hh:mm:ss A")} (${timeZoneAbbreviation})`,
    ],
    ["Exported by:", `${exportedByFname} ${exportedByLname} <${exportedBy}>`],
    ["Filters:", filtersText],
    [], // Empty row to separate metadata from data
  ]);

  // Add headers to the worksheet before adding data
  const headers = Object.keys(mappedLogs[0] || {});
  XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: "A6" });
  // Add the actual data starting from row 7
  XLSX.utils.sheet_add_json(worksheet, mappedLogs, {
    origin: "A7",
    skipHeader: true,
  });
  const colWidths = headers.map((header) => {
    const columnData = mappedLogs.map((row) => row[header]?.toString() || "");
    const headerLength = header.length;
    const maxLength = Math.max(
      ...columnData.map((data) => data.length),
      headerLength,
    );
    return { wch: maxLength + 2 }; // Add some padding to the width
  });
  worksheet["!cols"] = colWidths;

  // Apply filters to the header row (row 6)
  const headerRange = `A6:${XLSX.utils.encode_col(headers.length - 1)}6`;
  worksheet["!autofilter"] = { ref: headerRange };

  // Add the sheet to the workbook
  workbook.SheetNames.push("data");
  workbook.Sheets.data = worksheet;

  // Write the workbook with compression
  const excelBuffer = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array",
    compression: true, // Enable compression to reduce file size
  });
  const saveAsExcelFile = (buffer, fileName) => {
    const myCurrentDate = new Date();
    // const date = `${
    //   myCurrentDate.getMonth() + 1
    // }/${myCurrentDate.getDate()}/${myCurrentDate.getFullYear()} ${myCurrentDate.getHours()}:${myCurrentDate.getMinutes()}:${myCurrentDate.getSeconds()}`;
    // const newCurrentDate = date;
    const EXCEL_TYPE =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
    const data = new Blob([buffer], { type: EXCEL_TYPE });
    FileSaver.saveAs(
      data,
      `${fileName}_${moment(myCurrentDate)
        .tz(localTimeZone)
        .format("MMDDYYYYhhmmss")}.xlsx`,
    );
  };
  saveAsExcelFile(excelBuffer, fileName);
};

export const exportToXLS = (mappedLogs, fileName) => {
  const fileType =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
  const fileExtension = ".xlsx";
  const ws = XLSX.utils.json_to_sheet(
    !isEmpty(mappedLogs) ? mappedLogs : [{ "No Records Found": "" }],
    { origin: "A1" },
  );
  const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
  if (!isEmpty(mappedLogs)) {
    ws["!autofilter"] = {
      ref: `A1:${XLSX.utils.encode_col(
        Object.keys(mappedLogs[0]).length - 1,
      )}1`,
    };
    ws["!sort"] = [{ column: 1, order: "asc" }];
  }

  const myCurrentDate = new Date();
  const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  // const date = `${
  //   myCurrentDate.getMonth() + 1
  // }/${myCurrentDate.getDate()}/${myCurrentDate.getFullYear()} ${myCurrentDate.getHours()}:${myCurrentDate.getMinutes()}:${myCurrentDate.getSeconds()}`;
  // const newCurrentDate = date;
  // Calculate the maximum content length for each column
  const columnWidths = [];
  XLSX.utils.sheet_to_json(ws, { header: 1 }).forEach((row) => {
    row.forEach((cell, colIndex) => {
      const cellLength = cell ? String(cell).length : 10; // Default width if cell is empty
      columnWidths[colIndex] = Math.min(
        Math.max(columnWidths[colIndex] || 0, cellLength),
        25,
      );
    });
  });

  // Set the width for each column based on the maximum content length
  ws["!cols"] = columnWidths.map((width) => ({ wch: width }));

  const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const data = new Blob([excelBuffer], { type: fileType });
  FileSaver.saveAs(
    data,
    `${fileName}${moment(myCurrentDate)
      .tz(localTimeZone)
      .format("MMDDYYYYhhmmss")}${fileExtension}`,
  );
};

// Function to capitalize the first letter of a string
export function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export const rtp = {
  Module_RTP: "Module_RTP",
  APIType_RTP: "APIType_RTP",
};

export const groupCheck = {
  rtp: {
    Module: "Module_RTP",
    APIType: "APIType_RTP",
  },
  ach: {
    Module: "Module_ACH",
    APIType: "APIType_ACH",
  },
  balancecheck: {
    Module: "Module_BCCheck",
    APIType: "APIType_BCheck",
  },
  statusupdate: {
    Module: "Module_StatusUpdate",
    APIType: "APIType_StatusUpdate",
  },
  transferreturn: {
    Module: "Module_Tr_Rtn",
    APIType: "APIType_Tr_Rtn",
  },
  vikexclude: {
    Module: "Module_VikExclude",
    APIType: "APIType_VikExclude",
  },
};

// export const groupCheck = {
//   module: {
//     Module_RTP: "Module_RTP",
//     Module_BCCheck: "Module_BCCheck",
//     Module_ACH: "Module_ACH",
//     Module_StatusUpdate: "Module_StatusUpdate",
//   },
//   apiType: {
//     APIType_RTP: "APIType_RTP",
//     APIType_BCheck:"APIType_BCheck",
//     APIType_ACH:"APIType_ACH",
//     APIType_StatusUpdate:"APIType_StatusUpdate"
//   },
// };

export const profileCodePermissionCheck = {
  profileCode: {
    VCS: "Ag", // currently Ag
    SA: "Mg", // currently Mg -Manager true
    ADM: "ADM", // make it Mg first -Manager false
    STD: "Sub-Mg", // sum manager
  },
};

export const statusCode = {
  submit: 3,
};

export const permissionCheck = {
  Category: {
    reports: 1,
    setup: 2,
    residual: 3,
  },
  Page: {
    merchant: 1,
    user: 2,
    roles: 3,
    groups: 4,
    merchantInvite: 6,
    pricingTemplate: 7,
    PayoutSummary: 11,
    PayoutDetail: 12,
    IncomeExpenseDetail: 13,
    AdjustmentDetails: 14,
    CalculationSummary: 15,
    ImportLogs: 16,
    CalculationHistory: 17,
    PricingSchedule: 1,
    PayoutApproval: 6,
    AgentPayout: 2,
    MerchantMapping: 3,
    PayoutCalculation: 4,
    Adjustment: 5,
    managerList: 34,
  },
  option: {
    view: 1,
    add: 2,
    edit: 3,
    export: 4,
    exportToPdf: 5,
    exportToExcel: 6,
    clone: 7,
    import: 8,
    changeStatus: 9,
    setPermissions: 10,
    associatedUsers: 11,
    resetPassword: 12,
    viewHierarchy: 13,
    allowToBoard: 14, // merchant boarding
    allowToChecklist: 15, // merchant boarding
    allowToLive: 16, // merchant boarding
    viewPricing: 17, // merchant boarding
    activityHistory: 18,
    additionalInformation: 19, // merchant boarding
    sendMerchantSignupLink: 20,
    history: 21, // Calculation history
    addAdjustment: 22,
    addStatusPermission: 23,
    updateDBA: 24,
    allowToChangeStatus: 25,
    saveDraft: 27,
    sendInvite: 28,
    inviteManager: 29,
    submit: 30,
    resendInvite: 31,
    asterisk: 32,
  },
};

export const MgStatus = {
  Invited: "Invited",
  Active: "Active",
  PendingActivation: "Pending Activation",
  Inactive: "Inactive",
  AwaitingBusinessSubmission: "Awaiting Business Submission",
  ServiceActivationPending: "Service Activation Pending",
};

export const ManagerType = {
  Sub: "sub",
  Main: "main",
};

export const serviceCodeValues = {
  RTP: "RTP",
  ACH: "ACH",
  VIKExclude: "VIKExclude",
  CPR: "CPR",
};

export const actionPermission = (catId, pageId, type) => {
  const roleData = JSON.parse(localStorage.getItem("rolePermission"));

  // let roles = roleData.roleTab.Roles_Data
  // if(roleData.roleTab){
  //   roles = roleData.roleTab.Roles_Data
  // }
  const roles = get(roleData, "roleTab.Roles_Data", false);
  // if (isEmpty(roles) || !catId || !pageId || !type) {
  //   return roles;
  // }
  if (!isEmpty(roles.Master_Permission)) {
    const mainCat = roles.Master_Permission.filter(
      (cat) => cat.Cat_Code === catId,
    );
    let permission = !!mainCat;
    if (!permission) {
      return false;
    }

    const pageDetails = mainCat[0]?.Page_Menu.filter(
      (page) => page.Page_Code === pageId,
    );
    permission = !!pageDetails;
    if (!permission) {
      return false;
    }
    const permissionData = pageDetails[0]?.Options.find(
      (per) => per.value === type,
    );
    if (permissionData) {
      return permissionData.permission;
    }
    return false;
  }
};

// Utility to format phone numbers
export function formatPhoneNumber(v) {
  let r = v.replace(/\D/g, "");
  if (r.length > 11) {
    r = r.replace(/^(\d\d\d)(\d{3})(\d{0,4}).*/, "$1-$2-$3");
    return r;
  }
  if (r.length > 6) {
    r = r.replace(/^(\d\d\d)(\d{3})(\d{0,4}).*/, "$1-$2-$3");
  } else if (r.length > 3) {
    r = r.replace(/^(\d\d\d)(\d{0,3})/, "$1-$2");
  } else {
    r = r.replace(/^(\d*)/, "$1");
  }
  return r;
  // // let val;
  // val = val.replace(/\D/g, "");
  // val = val.replace(/^(\d{3})/, "$1-");
  // val = val.replace(/-(\d{2})/, "-$1-");
  // val = val.replace(/(\d)-(\d{4}).*/, "$1-$2");
  // return val;
}

export const getQueryParams = (data) => {
  const queryParams = new URLSearchParams(window.location.search);
  return queryParams.get(data);
};

export const API_LOGS_DEFAULT_DATE_RANGE = {
  RTP: 7,
  ACH: 7,
  StatusUpdate: 7,
  TransferReturn: 7,
  BalanceCheck: 7,
  VIKExclude: 7,
};

export const API_LOGS_MAX_DATE_RANGE = {
  RTP: 2,
  ACH: 2,
  StatusUpdate: 2,
  TransferReturn: 2,
  BalanceCheck: 2,
  VIKExclude: 2,
};

export const formatDate = (date) => {
  return date.toISOString().split("T")[0];
};

export const getDefaultDateRange = (groupName) => {
  const endDate = new Date();
  const startDate = new Date(endDate);
  startDate.setDate(startDate.getDate() - getDateRange(groupName, "Days"));
  return {
    startDate: formatDate(startDate),
    endDate: formatDate(endDate),
  };
};

export const getValidValue = (...values) => {
  return (
    values.find(
      (value) =>
        value !== null && value !== undefined && String(value).trim() !== "",
    ) ?? ""
  );
};

export const getFormattedDate = (date) => {
  if (date instanceof Date) {
    return format(date, "yyyy-MM-dd");
  }

  if (typeof date === "string") {
    const parts = date.split("/");

    if (parts.length === 3) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
  }

  return null;
};

export const getAutoDateRange = (type, date, groupName) => {
  const formattedDate = getFormattedDate(date);
  if (!formattedDate) {
    return {
      date1: null,
      date2: null,
    };
  }
  const maxDateRange = getDateRange(groupName, "Months");
  const selectedDate = new Date(`${formattedDate}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (type === "from") {
    const calculatedEndDate = addMonths(selectedDate, maxDateRange);
    const finalEndDate = calculatedEndDate > today ? today : calculatedEndDate;
    return {
      date1: formattedDate,
      date2: format(finalEndDate, "yyyy-MM-dd"),
    };
  }
  const calculatedStartDate = subMonths(selectedDate, maxDateRange);
  return {
    date1: format(calculatedStartDate, "yyyy-MM-dd"),
    date2: formattedDate,
  };
};

export const isDateRangeValid = (date1, date2, groupName) => {
  if (!date1 || !date2) {
    return true;
  }

  const startDate = new Date(`${date1}T00:00:00`);
  const endDate = new Date(`${date2}T00:00:00`);

  // Invalid order
  if (startDate > endDate) {
    return false;
  }
  const maxDateRange = getDateRange(groupName, "Months");
  const maxEndDate = addMonths(startDate, maxDateRange);
  return endDate <= maxEndDate;
};

export const getDateRange = (groupName, type) => {
  let monthRange = type == "Months" ? API_LOGS_MAX_DATE_RANGE[groupName] : API_LOGS_DEFAULT_DATE_RANGE[groupName];
  try {
    const rolePermission = localStorage.getItem("rolePermission");
    if (rolePermission) {
      const data = JSON.parse(rolePermission);
      const lookupData = data?.lookupMasterData?.data
        ?.find((lookup) => lookup?.Lookup_Code === groupName)
        ?.LookUp_Detail?.find(
          (item) => item?.Lookup_Detail_Code === `${groupName}_${type}`,
        );

      const lookupMonths = Number(lookupData?.Lookup_Detail_Name);
      if (Number.isFinite(lookupMonths)) {
        monthRange = lookupMonths;
      }
    }
  } catch(err) {
    console.log("err", err)
  }

  return monthRange;
};

export const getExportFileName = (apiName) => {
  const now = new Date();
  const MM = String(now.getMonth() + 1).padStart(2, "0");
  const DD = String(now.getDate()).padStart(2, "0");
  const YYYY = now.getFullYear();
  const HH = String(now.getHours()).padStart(2, "0");
  const MIN = String(now.getMinutes()).padStart(2, "0");
  return `${apiName}Logs_${MM}${DD}${YYYY}_${HH}${MIN}.zip`;
};